import type { TrelloCard } from '@/types'

// ─── Month arithmetic ─────────────────────────────────────────────────────────

/**
 * Returns the month BEFORE the given month/year (0-indexed months).
 * Correctly wraps January (0) → December (11) of the previous year.
 *
 * Examples:
 *   getPreviousMonth(6, 2026)  →  { month: 5, year: 2026 }  // June
 *   getPreviousMonth(7, 2026)  →  { month: 6, year: 2026 }  // July
 *   getPreviousMonth(0, 2027)  →  { month: 11, year: 2026 } // December
 */
export function getPreviousMonth(
  month: number,
  year: number
): { month: number; year: number } {
  if (month === 0) {
    return { month: 11, year: year - 1 }
  }
  return { month: month - 1, year }
}

// ─── Date filtering ───────────────────────────────────────────────────────────

/**
 * Returns true if the card's due date falls within the given month/year.
 *
 * Uses UTC methods (getUTCMonth / getUTCFullYear) so the comparison is
 * consistent with the UTC date Trello stores, regardless of the browser's
 * local timezone. A card due "July 15" in Trello will always be counted
 * in July, even for users in UTC-3.
 *
 * Shared by every Operação-board calculation (demand costs included) so
 * there is a single, consistent month rule across the dashboard.
 */
export function cardDueInMonth(card: TrelloCard, month: number, year: number): boolean {
  if (!card.due) return false
  const d = new Date(card.due)
  if (isNaN(d.getTime())) return false
  return d.getUTCFullYear() === year && d.getUTCMonth() === month
}

// ─── Payment cycle (day 21 → day 20) ───────────────────────────────────────────

/**
 * Returns the [start, end] local-date range (day 21 of the previous month
 * through day 20 of `month`/`year`, inclusive on both ends) of the payment
 * cycle that is named after `month`/`year` — e.g. the cycle "Outubro/2026"
 * runs from 21/09/2026 to 20/10/2026. Correctly wraps January (0) →
 * December (11) of the previous year.
 */
export function getCycleRange(month: number, year: number): { start: Date; end: Date } {
  const prevMonth = month === 0 ? 11 : month - 1
  const prevYear = month === 0 ? year - 1 : year
  return {
    start: new Date(prevYear, prevMonth, 21),
    end: new Date(year, month, 20),
  }
}

/**
 * Returns true if the card's due date falls within the payment cycle named
 * after `month`/`year` (day 21 of the previous month → day 20 of
 * `month`/`year`, inclusive).
 *
 * Uses the browser's LOCAL date (getFullYear/getMonth/getDate), not UTC —
 * unlike cardDueInMonth above. A due date stored as UTC midnight can land
 * on the previous local day for viewers west of UTC; comparing by local
 * calendar date keeps the cycle boundary (day 21/day 20) matching what the
 * user actually sees as "today" in their own timezone.
 */
export function cardDueInCycle(card: TrelloCard, month: number, year: number): boolean {
  if (!card.due) return false
  const d = new Date(card.due)
  if (isNaN(d.getTime())) return false

  const { start, end } = getCycleRange(month, year)
  const localDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return localDay >= start && localDay <= end
}
